// context/OrderAlertsContext.jsx
// Admins only: unread new-order notifications (Uganda + DRC), checked every 20 s and when
// the window gets focus. Feeds the banner/chime, the badges on Admin → DwiSend and its
// Orders / DRC shop sections, and the NEW markers in both order lists. Display only:
// marking seen just marks the admin's own notification read — no order is touched.
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationAPI } from '../services/api';
import { orderAlertsFrom } from '../hooks/orderAlerts';

const EMPTY = { alerts: [], count: 0, countUG: 0, countDRC: 0, newRefs: new Set(), fresh: [], markSeen: async () => {}, refresh: () => {} };
const OrderAlertsContext = createContext(EMPTY);
export const POLL_MS = 20000;

export function OrderAlertsProvider({ enabled, children }) {
  const [alerts, setAlerts] = useState([]);
  const [fresh, setFresh] = useState([]);          // arrived since this page was opened → chime
  const known = useRef(null);                       // ids already seen by this tab (null = first load)

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await notificationAPI.getAll({ unread: 'true', limit: 100 });
      const list = orderAlertsFrom(r.notifications || []);
      if (known.current) {
        const arrived = list.filter(a => !known.current.has(String(a.id)));
        if (arrived.length) setFresh(arrived);
      }
      known.current = new Set(list.map(a => String(a.id)).concat(known.current ? [...known.current] : []));
      setAlerts(list);
    } catch { /* keep the last list */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const t = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [enabled, refresh]);

  // Opening an order marks its alert(s) read — the admin's own notification only.
  const markSeen = useCallback(async ref => {
    const mine = alerts.filter(a => a.ref === ref);
    if (!mine.length) return;
    setAlerts(prev => prev.filter(a => a.ref !== ref));
    await Promise.all(mine.map(a => notificationAPI.markRead(a.id).catch(() => {})));
  }, [alerts]);

  const value = {
    alerts, fresh, refresh, markSeen,
    count: alerts.length,
    countUG: alerts.filter(a => a.market === 'UG').length,
    countDRC: alerts.filter(a => a.market === 'DRC').length,
    newRefs: new Set(alerts.map(a => a.ref).filter(Boolean)),
  };
  return <OrderAlertsContext.Provider value={enabled ? value : EMPTY}>{children}</OrderAlertsContext.Provider>;
}

export const useOrderAlerts = () => useContext(OrderAlertsContext);
